<script setup>
import { computed, provide, toRef } from 'vue'

const props = defineProps({
  size: {
    type: String,
    default: 'md',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  wrapper: {
    type: String,
    default: 'ul'
  },
  type: {
    type: String,
    default: 'buttons'
  },
  /**
   * Equal-width columns for type="segmented" (Tailwind grid-cols-N).
   * Omit for content-sized inline pills.
   */
  cols: {
    type: [Number, String],
    default: null,
    validator: (v) => v == null || [2, 3, 4, 5, '2', '3', '4', '5'].includes(v),
  },
  customClasses: {
    type: String,
    default: ''
  },
})

const colsClassMap = {
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
  5: 'grid-cols-5',
}

const resolvedCols = computed(() => {
  if (props.cols == null || props.cols === '') return null
  return Number(props.cols)
})

provide('tabGroupCols', resolvedCols)
provide('tabGroupType', toRef(props, 'type'))
provide('tabGroupSize', toRef(props, 'size'))

const computedClass = computed(() => {
  if (props.type === 'lines') {
    return 'flex w-full sm:w-auto border-b border-b-surface-3 gap-x-3 max-w-full overflow-x-auto overflow-y-hidden overscroll-x-contain touch-pan-x no-scrollbar [&>*]:shrink-0'
  }
  if (props.type === 'buttons') return 'inline-flex flex-wrap !p-1 rounded bg-surface-2'
  if (props.type === 'segmented') {
    const cols = resolvedCols.value
    const layout = cols
      ? `grid w-full ${colsClassMap[cols] || 'grid-cols-2'}`
      : 'inline-flex items-center max-w-full'
    return [layout, 'gap-1 bg-surface-1 rounded-lg p-1', props.customClasses].filter(Boolean).join(' ')
  }
  if (props.type === 'custom') return props.customClasses
  return props.customClasses
})
</script>

<template>
  <component :is="props.wrapper" :class="computedClass">
      <slot />
  </component>
</template>

<style scoped>
/* Hide scrollbar for Chrome, Safari and Opera */
.no-scrollbar::-webkit-scrollbar {
    display: none;
}

/* Hide scrollbar for IE, Edge and Firefox */
.no-scrollbar {
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;  /* Firefox */
}
</style>
